// MapLinkCoordinatesImport — optional map-URL helper beside lat/lon.
// Transient UI state only; does not persist the pasted URL.
//
// Props:
//   idPrefix: string
//   onCoordinatesImported: ({ latitude, longitude }) => void

import { useEffect, useRef, useState } from "react";

import { resolveMapLinkCoordinates } from "../api/geo";
import {
  MAP_LINK_PARSE_MESSAGES,
  parseMapLinkCoordinates,
} from "../lib/parseMapLinkCoordinates";

const STATUS_SUCCESS = "Координати отримано";
const STATUS_LOADING = "Отримання координат…";
const STATUS_NETWORK = "Не вдалося отримати координати з посилання";

function MapLinkCoordinatesImport({ idPrefix = "loc", onCoordinatesImported }) {
  const [mapUrl, setMapUrl] = useState("");
  const [pending, setPending] = useState(false);
  const [status, setStatus] = useState({ tone: "", message: "" });
  const requestIdRef = useRef(0);
  const abortRef = useRef(null);

  useEffect(() => {
    return () => {
      requestIdRef.current += 1;
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, []);

  const inputId = `${idPrefix}-map-link`;
  const statusId = `${idPrefix}-map-link-status`;

  const invalidateInFlight = () => {
    requestIdRef.current += 1;
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setPending(false);
  };

  const handleUrlChange = (event) => {
    invalidateInFlight();
    // Any URL edit clears prior loading/success/error; coordinates stay untouched.
    setStatus({ tone: "", message: "" });
    setMapUrl(event.target.value);
  };

  const applyCoordinates = (latitude, longitude) => {
    onCoordinatesImported({
      latitude: String(latitude),
      longitude: String(longitude),
    });
    setStatus({ tone: "success", message: STATUS_SUCCESS });
  };

  const handleImport = async () => {
    if (pending) return;

    const parsed = parseMapLinkCoordinates(mapUrl);
    if (parsed.ok) {
      applyCoordinates(parsed.latitude, parsed.longitude);
      return;
    }

    if (!parsed.needsResolve) {
      setStatus({
        tone: "error",
        message: parsed.message || MAP_LINK_PARSE_MESSAGES.unsupported,
      });
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setPending(true);
    setStatus({ tone: "loading", message: STATUS_LOADING });

    try {
      const result = await resolveMapLinkCoordinates(parsed.url, {
        signal: controller.signal,
      });
      if (requestId !== requestIdRef.current) return;
      applyCoordinates(result.latitude, result.longitude);
    } catch (error) {
      if (controller.signal.aborted || requestId !== requestIdRef.current) {
        return;
      }
      const detail = error?.response?.data?.detail;
      const message =
        typeof detail === "string" && detail.trim()
          ? detail
          : STATUS_NETWORK;
      setStatus({ tone: "error", message });
    } finally {
      if (requestId === requestIdRef.current) {
        setPending(false);
      }
    }
  };

  const statusClass =
    status.tone === "error" ? "form-hint text-danger" : "form-hint";

  return (
    <div className="ap-row">
      <div className="ap-field ap-field--wide">
        <label htmlFor={inputId}>Посилання на карту</label>
        <input
          id={inputId}
          name="map_link"
          type="url"
          className="form-control"
          inputMode="url"
          autoComplete="off"
          placeholder="Google Maps, Apple Maps або OpenStreetMap"
          value={mapUrl}
          onChange={handleUrlChange}
          aria-describedby={status.message ? statusId : undefined}
        />
        <p className="ap-helper">
          <button
            type="button"
            className="action-btn"
            onClick={handleImport}
            disabled={pending}
            aria-busy={pending ? "true" : "false"}
          >
            Отримати координати
          </button>
        </p>
        {status.message ? (
          <small
            id={statusId}
            className={statusClass}
            role="status"
            aria-live="polite"
            data-tone={status.tone || undefined}
          >
            {status.message}
          </small>
        ) : null}
        <small className="form-hint">
          Необовʼязково. Очищення посилання не змінює вже введені координати.
        </small>
      </div>
    </div>
  );
}

export default MapLinkCoordinatesImport;
